"use client";

import React, { useRef } from "react";
import Image from "next/image";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ContainerProp, descriptionObject } from "../../../global";
import purpleBG from "@/app/assets/purple-gradient-bg.jpg";
import { textTurquoise } from "@/app/utils/api-helpers";
import BottomCurves from "../curves/bottomCurves";
import BannerFloatingArrow from "../uiDecor/bannerFloatingArrow";

type BannerDescContainerProp = Omit<ContainerProp, "className"> & {
    waveBgColor: string;
    description: descriptionObject[];
};

export default function BannerDescContainer({ serviceName, header, description, imageSrc, altText, waveBgColor }: BannerDescContainerProp) {
    const containerRef = useRef(null);
    const imgRef = useRef(null);

    useGSAP(
        () => {
            gsap.to(imgRef.current, { opacity: 1, stagger: 0.3, y: 0 });
            gsap.to(".textChild", { opacity: 1, stagger: 0.3, y: 0 });
        },
        { scope: containerRef },
    );

    return (
        <section className={`min-h-[560px] pb-20 pt-24 sm:pt-32 lg:pt-[3.969vw] ${description ? "md:h-[46lvw]" : "md:h-[42lvw]"}`}>
            <Image
                src={purpleBG}
                width={1920}
                height={790}
                alt="background image"
                className={`absolute top-0 left-0 right-0 bottom-0 w-full h-full pointer-events-none -z-10 object-cover`}
            />

            <div className="flex flex-col md:flex-row lg:items-center" ref={containerRef}>
                <div className="flex-1">
                    {serviceName && <h6 className="textChild opacity-0 translate-y-8">{serviceName}</h6>}
                    {header && <h1 className="textChild opacity-0 translate-y-8">{textTurquoise(header)}</h1>}
                    {description?.map((desc, index) => (
                        <div key={index} className=" lg:w-10/12">
                            {desc.type === "paragraph" && (
                                <p className="textChild text-white opacity-0 translate-y-8">
                                    {desc.children.map((para, index) => (
                                        <span key={index} className=" text-inherit">
                                            {para.text}
                                        </span>
                                    ))}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
                <div className="flex-1">
                    {imageSrc && (
                        <Image
                            src={imageSrc}
                            width={600}
                            height={600}
                            alt={altText || ""}
                            className="opacity-0 translate-y-8 w-11/12 m-auto md:w-full h-auto"
                            ref={imgRef}
                            loading="lazy"
                        />
                    )}
                </div>
            </div>
            <BannerFloatingArrow direction={"left"} className={`-bottom-4 md:-bottom-8`} />
            <BottomCurves color={waveBgColor} direction="right" />
        </section>
    );
}
